import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Phone, X, ChevronRight, Clock, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const WhileYouWereGoneWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  // Check if widget was dismissed for current session
  useEffect(() => {
    const sessionId = localStorage.getItem('sessionId');
    const dismissedSession = localStorage.getItem('whileYouWereGoneDismissed');
    if (sessionId && dismissedSession === sessionId) {
      setDismissed(true);
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/dashboard/while-you-were-gone');
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch while-you-were-gone data:', err);
        // Don't show error for 404 - endpoint might not exist yet
        if (err.response?.status !== 404) {
          setError('Failed to load activity summary');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dismissed]);

  const handleDismiss = () => {
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      localStorage.setItem('whileYouWereGoneDismissed', sessionId);
    }
    setDismissed(true);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Don't render if dismissed
  if (dismissed) return null;

  // Don't render while loading
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-indigo-900/60 via-purple-900/50 to-indigo-900/60 backdrop-blur-sm border border-indigo-500/30 rounded-2xl p-5 mb-8">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
        </div>
      </div>
    );
  }

  // Don't render if error or no data
  if (error || !data) return null;

  // Don't render if no activity to show
  const totalActivity = (data.emails?.count || 0) + (data.sigil?.count || 0) + (data.calls?.count || 0);
  if (totalActivity === 0) return null;

  return (
    <div className="bg-gradient-to-r from-indigo-900/60 via-purple-900/50 to-indigo-900/60 backdrop-blur-sm border border-indigo-500/30 rounded-2xl overflow-hidden mb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600/30 to-purple-600/30 px-5 py-3 flex items-center justify-between border-b border-indigo-500/20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Clock className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">While You Were Gone</h2>
            <p className="text-xs text-indigo-300">
              Last login: {formatTimeAgo(data.last_login)}
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1.5 text-indigo-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          title="Dismiss until next login"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Emails Section */}
          <div
            onClick={() => navigate('/mail')}
            className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/30 rounded-xl p-4 cursor-pointer transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-medium">Emails</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">{data.emails?.count || 0}</span>
              <span className="text-sm text-slate-400">new</span>
            </div>
            {(data.emails?.unread_count || 0) > 0 && (
              <div className="mt-2 text-sm text-blue-300">
                {data.emails.unread_count} unread
              </div>
            )}
            {data.emails?.recent?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-slate-400 mb-2">Recent:</p>
                <div className="space-y-1.5">
                  {data.emails.recent.slice(0, 2).map((email, idx) => (
                    <p key={idx} className="text-xs text-slate-300 truncate">
                      {email.subject || email.from_address}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sigil Section */}
          <div
            onClick={() => navigate('/sigil')}
            className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/30 rounded-xl p-4 cursor-pointer transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-medium">Sigil</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">{data.sigil?.count || 0}</span>
              <span className="text-sm text-slate-400">messages</span>
            </div>
            {(data.sigil?.unread_count || 0) > 0 && (
              <div className="mt-2 text-sm text-emerald-300">
                {data.sigil.unread_count} unread
              </div>
            )}
            {data.sigil?.channels_with_activity?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-slate-400 mb-2">Active channels:</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.sigil.channels_with_activity.slice(0, 3).map((channel, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full"
                    >
                      #{channel.name || channel}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Phone Calls Section */}
          <div
            onClick={() => navigate('/voip')}
            className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/30 rounded-xl p-4 cursor-pointer transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-medium">Phone</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">{data.calls?.count || 0}</span>
              <span className="text-sm text-slate-400">calls</span>
            </div>
            {(data.calls?.missed || 0) > 0 && (
              <div className="mt-2 text-sm text-red-300">
                {data.calls.missed} missed
              </div>
            )}
            {data.calls?.recent?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-slate-400 mb-2">Recent:</p>
                <div className="space-y-1.5">
                  {data.calls.recent.slice(0, 2).map((call, idx) => (
                    <p key={idx} className="text-xs text-slate-300 truncate">
                      {call.caller_name || call.caller_number}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {(data.calls?.count || 0) === 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-slate-500 italic">Coming soon</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhileYouWereGoneWidget;
